import React from 'react';
import { Table, Typography } from 'amphitheatre';
import { Prize, PrizeWrapper, Trophy, SaberLogo } from './FAQ.styled';
import { AiOutlineTrophy, AiOutlineSmile } from 'react-icons/ai';

import Saber from './_img_saber_logo.png';

const { Header, Paragraph } = Typography;

const SubStyle: React.CSSProperties = {
  fontWeight: 'bold',
  marginTop: '1.5em',
};

const columns = [
  {
    id: 'weight',
    text: 'Weight',
  },
  {
    id: 'component',
    text: 'Component',
  },
];

const data = [
  {
    id: 1,
    weight: '70%',
    component: (
      <>
        The total addressable market in dollar value of the constellation
        simulated using P.I.G.I. and matched in the spreadsheet
      </>
    ),
  },
  {
    id: 2,
    weight: '30%',
    component: (
      <>The viability of the business/mission idea (2 page business proposal)</>
    ),
  },
];

const FAQContent: React.FunctionComponent = () => {
  return (
    <>
      <Header>Frequently Asked Questions</Header>
      {/*
        ******************************
        WHAT IS P.I.G.I
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        What is P.I.G.I?
      </Header>
      <Paragraph>
        P.I.G.I stands for the ‘Predictive Interactive Ground station Interface’
        and is Saber Astronautics’ software currently used in spacecraft
        operations and mission design. The version used for{' '}
        <i>Quest For Blue</i> is the “Casual” licence-- meant for training and
        basic mission planning. We use more advanced versions of P.I.G.I in our
        mission control centres in Colorado and Australia to control the actual
        satellites and track debris. Using the Casual licence for{' '}
        <i>Quest For Blue</i> gives you a head-start in the basic capabilities.
      </Paragraph>
      <Paragraph>
        <a href="https://saberastro.com/pigi">
          To find out more about P.I.G.I. visit our website!
        </a>
      </Paragraph>
      {/*
        ******************************
        Who’s eligible to join the “quest for blue”?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        Who’s eligible to join the <i>Quest For Blue</i>?
      </Header>
      <Paragraph>
        Entrants to the <i>Quest For Blue</i> are{' '}
        <b>not limited by age or education</b>. P.I.G.I. itself will require an
        internet connection and a computer with a reasonable graphics card.
        Other aspects of the competition can be completed offline with the final
        documents to be submitted online when we get close to the due date.
      </Paragraph>
      <Paragraph>
        The competition is intended for undergraduate students studying some
        form of space engineering. However, we encourage anyone interested in
        space to give it a go, including high schoolers, hobbyists, and
        enthusiasts.
      </Paragraph>
      <Paragraph>
        We require participants under the age of 18 to have a guardian or
        representative register.
      </Paragraph>
      {/*
        ******************************
        Why do I need to register again when I have already pre-registered?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        Why do I need to register again when I have already pre-registered?
      </Header>
      <Paragraph>
        Your details are already registered for the <i>Quest For Blue</i>{' '}
        competition. To access downloads such as the P.I.G.I. software,
        competition templates and tutorials, you’ll need a Saber Astronautics
        account.
      </Paragraph>
      {/*
        ******************************
        What prizes are available?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        What prizes are available?
      </Header>
      <PrizeWrapper>
        <Prize>
          <Trophy prize="gold">
            <AiOutlineTrophy />
          </Trophy>
          1st Prize: $2,000 AUD
        </Prize>
        <Prize>
          <Trophy prize="silver">
            <AiOutlineTrophy />
          </Trophy>
          2nd Prize: $1,500 AUD
        </Prize>
        <Prize>
          <Trophy prize="bronze">
            <AiOutlineTrophy />
          </Trophy>
          3rd Prize: $1,000 AUD
        </Prize>
      </PrizeWrapper>
      <Prize style={{ marginTop: '0.5rem' }}>
        <Trophy prize="notable">
          <AiOutlineSmile />
        </Trophy>
        Notable Work: 2x $250 AUD
      </Prize>
      {/*
        ******************************
        What will the submission look like?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        What will the submission look like?
      </Header>
      <Paragraph>
        Competition submissions consist of 3 files. Two files are from provided
        templates and the third is a P.I.G.I. generated save file. The first
        file is the 3-page business overview in the provided template. The
        second file is a spreadsheet where you convert your overpasses to the
        Total Addressable Market. The third and final file will be the P.I.G.I.
        scene save file depicting your mission which will be in JSON format.{' '}
        <b>
          The link to submit your response will be available at the end of the
          competition.
        </b>
      </Paragraph>
      {/*
        ******************************
        How are submissions judged?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        How are submissions judged?
      </Header>
      <Paragraph>Submissions are judged by the following:</Paragraph>
      <Paragraph>
        <Table
          style={{
            borderRadius: '15px',
            boxShadow: '5px 5px 10px #050505, -5px -5px 10px #101010',
          }}
          columns={columns}
          data={data}
        />
      </Paragraph>
      {/*
        ******************************
        Can we enter a team?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        Can we enter a team?
      </Header>
      <Paragraph>
        <i>Quest For Blue</i> is intended for individual entries. We are only
        sending prize money to an individual or the legal guardian of that
        individual.
      </Paragraph>
      {/*
        ******************************
        How many entries can I submit?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        How many entries can I submit?
      </Header>
      <Paragraph>
        Only one entry per person is allowed. Submitting multiple entries will
        result in forfeiting your entries. Play nice!
      </Paragraph>
      {/*
        ******************************
        But I’m in industry and want to play! Can I join in on the fun?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        But I’m in industry and want to play! Can I join the fun?
      </Header>
      <Paragraph>
        Of course you can! We’re glad you’re as excited as we are. Contact us at
        <a href="mailto:quest@saberastro.com">quest@saberastro.com</a> about
        becoming a moderator or judge for this competition. Members from
        established industry and corporate representatives are not eligible for
        prizes. Individuals from pre-funded startups are allowed to enter as if
        you were university students.
      </Paragraph>
      {/*
        ******************************
        Are there any costs associated with entering the competition?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        Are there any costs associated with entering the competition?
      </Header>
      <Paragraph>
        No, all you need is an internet connection, a computer, and your dreams
        of a space startup.
      </Paragraph>
      {/*
        ******************************
        What resources will be made available to me?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        What resources will be made available to me?
      </Header>
      <Paragraph>
        Saber Astronautics will provide resources and templates to guide you
        along. This includes a P.I.G.I. subscription for the month long duration
        of the competition. We are developing tutorials geared towards
        participants, covering aspects of using P.I.G.I. and the purpose of the
        components of the submissions.
      </Paragraph>
      {/*
        ******************************
        I don’t have prior experience with space operations, can I still
        participate?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        I don’t have prior experience with space operations, can I still
        participate?
      </Header>
      <Paragraph>
        Of course! This competition is geared towards students. We’re not
        looking for experience, we’re looking to pass on our knowledge and
        experience.
      </Paragraph>
      {/*
        ******************************
        Can I send my ideas to an investor?
        ******************************
      */}
      <Header level={3} style={SubStyle}>
        Can I send my ideas to an investor?
      </Header>
      <Paragraph>
        Of course you can. However we will caution&mdash; the level of fidelity
        we’re asking for in <i>Quest For Blue</i> submissions are, in our
        experience, lower than what is normally required to justify an
        investment, but good enough to gauge a concept’s value. Think of it like
        a good starting point to justify the effort an investor is likely to
        ask, once you’ve whet their appetite.
      </Paragraph>
      <Paragraph>
        That said, Saber actively participates in the entrepreneurial community.
        Investors often ask us to introduce them to new opportunities. We would
        love to see the <i>Quest For Blue</i> as a springboard for the future.
      </Paragraph>
      <Header level={4}>
        Need further information? Come check in with us on Facebook with our
        <a href="https://www.facebook.com/groups/questforblue">
          Quest For Blue
        </a>
        community.
      </Header>
      <hr />
      <Paragraph style={{ textAlign: 'center' }}>
        Sydney, Australia&nbsp;&nbsp;|&nbsp;&nbsp;Adelaide,
        Australia&nbsp;&nbsp;|&nbsp;&nbsp;Colorado, USA
      </Paragraph>
      <Paragraph style={{ textAlign: 'center' }}>
        <SaberLogo src={Saber} />
      </Paragraph>
    </>
  );
};

export default FAQContent;
